import { Button, Chip, Popper, PopperProps, TextField } from '@material-ui/core';
import Autocomplete, { RenderInputParams } from '@material-ui/lab-old/Autocomplete';
import React from 'react';
import styled from 'styled-components';
import { ICourse } from 'types/courses';

type IProps = {
  options: ICourse[] | null;
  value: ICourse[];
  onChange(value: ICourse[]): void;
  open: boolean;
  onOpen(): void;
  onClose(event: React.ChangeEvent<unknown>, reason?: string): void;
  className?: string;
};

const matchesQuery = (course: ICourse, query: string): boolean => {
  if (!query) {
    return true;
  }
  const haystack = `${course.code} ${course.name}`.toLowerCase();
  return query
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean)
    .every((term) => haystack.includes(term));
};

const getOptionLabel = (course: ICourse) => course.name;

// The dropdown is wider than the input so code + name fit on one line.
const WidePopper: React.FC<PopperProps> = (props) => (
  <Popper {...props} style={{ ...props.style, minWidth: 560 }} placement="bottom-start" />
);

// Multi-select for Health courses. Unlike the shared SelectLayout, the filter text is
// controlled here and survives each selection, so several matching courses can be
// picked in a row without retyping.
const HealthCourseSelect: React.FC<IProps> = ({ options, value, onChange, open, onOpen, onClose, className }) => {
  const [inputValue, setInputValue] = React.useState('');
  const loading = options === null;
  const allOptions = options ?? [];

  const handleInputChange = React.useCallback(
    (_event: React.ChangeEvent<unknown>, newValue: string, reason: string) => {
      // 'reset' fires after every selection in multiple mode; ignoring it keeps the filter text.
      if (reason === 'input') {
        setInputValue(newValue);
      }
    },
    [],
  );

  const filterOptions = React.useCallback(
    (candidates: ICourse[], state: { inputValue: string }) =>
      candidates.filter((course) => matchesQuery(course, state.inputValue)),
    [],
  );

  const filtered = React.useMemo(
    () => allOptions.filter((course) => matchesQuery(course, inputValue)),
    [allOptions, inputValue],
  );
  const selectedIds = React.useMemo(() => new Set(value.map((course) => course.id)), [value]);
  const filteredNotSelected = filtered.filter((course) => !selectedIds.has(course.id));

  const handleSelectFiltered = React.useCallback(() => {
    onChange([...value, ...filteredNotSelected]);
  }, [onChange, value, filteredNotSelected]);

  const handleClear = React.useCallback(() => {
    onChange([]);
    setInputValue('');
  }, [onChange]);

  const renderTags = (tagValue: ICourse[], getTagProps: (params: { index: number }) => Record<string, unknown>) =>
    tagValue.map((course, index) => (
      <CourseChip
        size="small"
        label={course.code || course.name}
        title={course.name}
        {...getTagProps({ index })}
        key={course.id}
      />
    ));

  const renderOption = (course: ICourse) => (
    <OptionRow>
      <OptionCode>{course.code}</OptionCode>
      <OptionName>{course.name}</OptionName>
    </OptionRow>
  );

  return (
    <Wrapper className={className}>
      <AutocompleteStyled
        multiple
        open={open}
        onOpen={onOpen}
        onClose={onClose}
        options={allOptions}
        value={value}
        onChange={(_event: React.ChangeEvent<unknown>, newValue: ICourse[] | null) => onChange(newValue ?? [])}
        inputValue={inputValue}
        onInputChange={handleInputChange}
        filterOptions={filterOptions}
        getOptionLabel={getOptionLabel}
        getOptionSelected={(option: ICourse, selected: ICourse) => option.id === selected.id}
        renderOption={renderOption}
        renderTags={renderTags}
        PopperComponent={WidePopper}
        disableCloseOnSelect
        loading={loading}
        loadingText={<span>Loading...</span>}
        noOptionsText={inputValue ? `No courses match “${inputValue}”` : 'No courses'}
        renderInput={(params: RenderInputParams) => (
          <TextField
            {...params}
            size="small"
            variant="outlined"
            label={`Courses${value.length ? ` (${value.length} selected)` : ''}`}
            placeholder="Filter by code or name…"
          />
        )}
      />
      <Helpers>
        <Button size="small" onClick={handleSelectFiltered} disabled={filteredNotSelected.length === 0}>
          {inputValue ? `Select ${filteredNotSelected.length} matching` : `Select all (${filteredNotSelected.length})`}
        </Button>
        <Button size="small" onClick={handleClear} disabled={value.length === 0 && !inputValue}>
          Clear
        </Button>
      </Helpers>
    </Wrapper>
  );
};

export default HealthCourseSelect;

const Wrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const AutocompleteStyled = styled(Autocomplete)`
  flex: 1 1 auto;
  min-width: 0;
`;

const Helpers = styled.div`
  display: flex;
  flex: 0 0 auto;
  gap: 4px;

  & .MuiButton-root {
    text-transform: none;
    font-size: 12px;
    padding: 0 6px;
    min-height: 22px;
  }
`;

const CourseChip = styled(Chip)`
  && {
    height: 22px;
    font-size: 12px;
    margin: 2px;
  }
`;

const OptionRow = styled.span`
  display: flex;
  gap: 12px;
  align-items: baseline;
  font-size: 13px;
  line-height: 1.3;
  white-space: nowrap;
  overflow: hidden;
`;

const OptionCode = styled.span`
  font-family: monospace;
  font-size: 12px;
  flex: 0 0 150px;
  opacity: 0.75;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const OptionName = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
`;
